import { Component, computed, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { AssistantTableStateService } from '@core/services/assistant/assistant-table-state.service';
import { EntityDisplayNamesService } from '@core/services/entity-display-names/entity-display-names.service';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { selectPlanChanges } from '@core/store/plan-edit/plan-edit-session.selectors';
import { PlanFindingsActions } from '@core/store/plan-findings/plan-findings.actions';
import {
  selectFindingsAreStale,
  selectFindingsBusy,
  selectFindingsPhase,
  selectPlanFindingsInView,
  selectPlanFindingsLive,
  selectPlanNeverChecked
} from '@core/store/plan-findings/plan-findings.selectors';

import { OccurrenceFilter } from '../../../findings-panel/findings-filtering';
import { PlanFindingsPanelComponent } from '../../../findings-panel/plan-findings-panel.component';
import {
  EntitySelection,
  RefResolver,
  citesSelection,
  contextEntityTypeOf
} from './finding-selection';

type Scope = 'all' | 'selected';

@Component({
  selector: 'app-findings-section',
  imports: [PlanFindingsPanelComponent, TranslatePipe],
  templateUrl: './findings-section.component.html',
  styles: ``
})
export class FindingsSectionComponent {
  private readonly store = inject(Store);
  private readonly tableState = inject(AssistantTableStateService).state;
  private readonly visualFocus = inject(VisualFocusService);
  protected readonly entityTypeLabels = inject(EntityDisplayNamesService)
    .singularDisplayNames;

  protected readonly findings = this.store.selectSignal(selectPlanFindingsInView);
  protected readonly live = this.store.selectSignal(selectPlanFindingsLive);
  protected readonly busy = this.store.selectSignal(selectFindingsBusy);
  protected readonly neverChecked = this.store.selectSignal(selectPlanNeverChecked);
  protected readonly stale = this.store.selectSignal(selectFindingsAreStale);
  protected readonly phase = this.store.selectSignal(selectFindingsPhase);
  private readonly changes = this.store.selectSignal(selectPlanChanges);
  /** The live findings still describe the plan before the change owed an answer. */
  protected readonly muted = computed(() => this.changes()?.settled === false);

  private readonly scope = signal<Scope>('all');

  protected readonly selectedOffered = computed(() => this.tableState() !== null);
  /** Under select all only the fetched page's uids are known, so a match would be partial. */
  protected readonly selectedDisabled = computed(
    () => this.tableState()?.allMatching === true
  );
  protected readonly activeScope = computed<Scope>(() =>
    this.scope() === 'selected' && this.selectedOffered() && !this.selectedDisabled()
      ? 'selected'
      : 'all'
  );

  private readonly selection = computed<EntitySelection | null>(() => {
    const table = this.tableState();
    if (table === null) return null;
    const contextEntityType = contextEntityTypeOf(table.entityType);
    // Marker clicks can leave non-route ids in the aggregator filter; only a known route narrows.
    const pinned = this.visualFocus.pinnedRouteUid();
    const narrowed =
      contextEntityType === 'ROUTE' &&
      pinned !== null &&
      this.visualFocus.knownRouteUids().has(pinned);
    return { contextEntityType, uids: new Set(narrowed ? [pinned] : table.uids) };
  });

  protected readonly occurrenceFilter = computed<OccurrenceFilter | null>(() => {
    const selection = this.selection();
    if (this.activeScope() !== 'selected' || selection === null) return null;
    const resolve: RefResolver | undefined =
      selection.contextEntityType === 'ROUTE'
        ? (ref) => this.visualFocus.routesOfRef(ref)
        : undefined;
    return (occurrence) => citesSelection(occurrence.refs, selection, resolve);
  });

  protected readonly hiddenNoteKey = computed(() =>
    this.activeScope() === 'selected' ? 'features.plan.panel.findings.hidden' : null
  );

  protected setScope(scope: Scope): void {
    this.scope.set(scope);
  }

  protected resetScope(): void {
    this.scope.set('all');
  }

  protected requestCheck(): void {
    this.store.dispatch(PlanFindingsActions.checkAsked());
  }
}
